import { Box, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { brand } from "../../../shared/theme/app-theme";
import { getReportStatusLabel, ReportStatus } from "../../report/models";
import { ReportStatusColors } from "../../report/report-status/report-status-indicator";
import { DashboardDTO } from "../model";

type Props = {
  data?: DashboardDTO;
};

type Slice = {
  status: ReportStatus;
  label: string;
  value: number;
  color: string;
};

const SIZE = 196;
const STROKE = 26;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;

const PeriodStatusChart: React.FC<Props> = ({ data }) => {
  const slices = useMemo<Slice[]>(() => {
    return (data?.dashboard ?? [])
      .filter((item) => item.status !== "TOTAL")
      .map((item) => {
        const status = item.status as ReportStatus;
        return {
          status,
          label: getReportStatusLabel(status),
          value: item.data?.total ?? 0,
          color: status
            ? ReportStatusColors[status] ?? brand.muted
            : ReportStatusColors[ReportStatus.LOADED],
        };
      });
  }, [data]);

  const total =
    data?.total ?? slices.reduce((sum, slice) => sum + slice.value, 0);
  const max = Math.max(1, ...slices.map((slice) => slice.value));
  const active = slices.filter((slice) => slice.value > 0);

  const arcs = useMemo(() => {
    if (total <= 0 || active.length === 0) {
      return [];
    }
    let offset = 0;
    return active.map((slice) => {
      const length = (slice.value / total) * CIRC;
      const arc = { ...slice, length, offset };
      offset += length;
      return arc;
    });
  }, [active, total]);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 4,
        px: 2.5,
        py: 2.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: SIZE,
          height: SIZE,
          flex: "0 0 auto",
        }}
      >
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={brand.line}
            strokeWidth={STROKE}
          />
          {arcs.map((arc) => (
            <circle
              key={arc.status}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={arc.color}
              strokeWidth={STROKE}
              strokeLinecap="butt"
              strokeDasharray={`${arc.length} ${CIRC - arc.length}`}
              strokeDashoffset={-arc.offset}
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            >
              <title>
                {arc.label}: {arc.value}
              </title>
            </circle>
          ))}
        </svg>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.1, color: brand.ink }}
          >
            {total}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Всего
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: "1 1 360px", minWidth: 280, display: "grid", gap: 1 }}>
        {slices.map((slice) => {
          const width = total > 0 ? (slice.value / max) * 100 : 0;
          return (
            <Box
              key={slice.status}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(140px, 1fr) 36px minmax(80px, 2fr)",
                alignItems: "center",
                gap: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  color: slice.value > 0 ? brand.ink : brand.muted,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={slice.label}
              >
                {slice.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: slice.value > 0 ? 600 : 400,
                  color: slice.value > 0 ? brand.ink : brand.muted,
                  textAlign: "right",
                }}
              >
                {slice.value}
              </Typography>
              <Box
                sx={{
                  height: 10,
                  borderRadius: 999,
                  bgcolor: brand.bg,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${width}%`,
                    bgcolor: slice.color,
                    borderRadius: 999,
                    opacity: slice.value > 0 ? 1 : 0,
                    transition: "width 240ms ease-out",
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PeriodStatusChart;
